import { Link } from 'react-router-dom'
import { TERMS_VERSION } from '../legal'

/**
 * The privacy policy, at the public `/privacy`.
 *
 * Google Play requires the URL on the store entry, and it must open without an account — hence
 * its place in the signed-out routes. Kept deliberately specific to what this app actually does:
 * a generic policy that claims analytics or ad partners the app has never had would be worse
 * than none, because it would be untrue.
 */
export function PrivacyPage() {
  return (
    <div className="page page-narrow legal">
      <div className="page-head">
        <div>
          <Link to="/" className="muted">
            ← Back
          </Link>
          <h1 style={{ marginTop: 6 }}>Privacy policy</h1>
          <div className="muted">Version {TERMS_VERSION} · My Tracker, by Highveld Tech (Pty) Ltd</div>
        </div>
      </div>

      <div className="card form-card">
        <h2>The short version</h2>
        <p>
          My Tracker records your runs and keeps them for you. Your data is not sold, not shared
          with advertisers, and not used to profile you. There is no analytics or tracking SDK in
          the app. You can delete everything at any time, and deletion means deletion.
        </p>

        <h2>Who we are</h2>
        <p>
          My Tracker is operated by <strong>Highveld Tech (Pty) Ltd</strong>, South Africa. For any
          question about this policy or your data, write to{' '}
          <strong>support@mytracker.fitness</strong>.
        </p>

        <h2>What we collect</h2>
        <p>
          <strong>Your account.</strong> Email address, the name you choose to display, and your
          password. The password is stored only as a bcrypt hash — we cannot read it, and we
          cannot tell you what it is.
        </p>
        <p>
          <strong>Your profile, if you fill it in.</strong> Height, weight, date of birth and
          gender. All four are optional. Weight is used to estimate the calories for a run; the
          others are stored for features that use them. You can clear any of them at any time.
        </p>
        <p>
          <strong>Your activities.</strong> For each recording: GPS positions and altitude, the
          time of each point, distance, speed and pace, lap splits, step cadence, any notes you
          add, and the name of the device that produced it. If you pair a heart-rate strap, the
          heart-rate readings are recorded too. The original .tcx file is kept byte for byte so you
          can always export your run unchanged.
        </p>
        <p>
          <strong>Technical records.</strong> Your IP address appears in web-server logs and is
          used to rate-limit sign-in attempts and uploads, which is how we keep the service from
          being abused. These logs rotate and are not linked to your account for any other purpose.
        </p>

        <h2>Location, specifically</h2>
        <p>
          The app collects <strong>precise location</strong> while you are recording, and only
          then. It is what produces the route of your run. Recording continues with the screen off,
          which is why the app runs as a foreground service with a visible notification — if that
          notification is not showing, the app is not recording your location. The app does not
          collect location in the background when you are not recording.
        </p>

        <h2>Health and fitness data</h2>
        <p>
          Heart rate, cadence, distance, pace and the calorie estimate are health and fitness data.
          They are stored against your account so you can review your own runs. They are not shared
          with anyone, not used for advertising, and not sold under any circumstances.
        </p>

        <h2>Where it goes</h2>
        <p>
          Your data is held in a PostgreSQL database on a server operated by Highveld Tech (Pty) Ltd
          in South Africa. Everything between your phone, this website and that server travels over
          HTTPS. Access to the API requires a signed token issued when you sign in.
        </p>

        <h2>Who else sees it</h2>
        <p>
          <strong>Google Play Services</strong> supplies the location fixes on your phone, through
          Android's fused location provider. That is Android itself doing the positioning; it is
          governed by Google's own privacy policy.
        </p>
        <p>
          <strong>OpenStreetMap</strong> serves the map tiles when you view a route on this website.
          Your browser requests those tiles directly, so OpenStreetMap's tile servers see your IP
          address and the area of the map you are looking at. They do not receive your account or
          your route — only which tiles were asked for.
        </p>
        <p>
          <strong>Email.</strong> Password-reset messages are sent from our own mail server. No
          third-party email provider is involved.
        </p>
        <p>
          There are no advertising networks, no analytics providers, and no data brokers. We do not
          sell or rent your data, and we have no commercial arrangement that involves sharing it.
        </p>

        <h2>How long we keep it</h2>
        <p>
          Until you delete it. There is no automatic expiry — your runs are yours to keep as long
          as you want them. Password-reset tokens are the exception: they expire after 30 minutes
          and are swept away afterwards.
        </p>

        <h2>Deleting your data</h2>
        <p>
          You can delete your account from the Account tab in the app, or at{' '}
          <Link to="/delete-account">mytracker.fitness/delete-account</Link>. It removes your
          account, your profile and every activity on it, including the original files. It happens
          immediately, there is no grace period, and there is no backup copy we can restore from.
        </p>
        <p>
          Before deleting, you can export every run with the <em>Download TCX</em> button, which
          gives you the original file. It imports into Garmin Connect, Strava and anything else that
          reads TCX, so leaving does not mean losing your history.
        </p>

        <h2>Your rights</h2>
        <p>
          Under South Africa's POPIA — and equivalently under the GDPR if you are in the EU — you
          may ask what we hold about you, have it corrected, and have it erased. The app gives you
          all three directly: your runs are visible and downloadable, your profile is editable, and
          deletion is one page away. If you would rather ask us, write to{' '}
          <strong>support@mytracker.fitness</strong>.
        </p>

        <h2>Children</h2>
        <p>
          My Tracker is not directed at children under 13, and we do not knowingly create accounts
          for them. If you believe a child has an account here, write to us and we will remove it.
        </p>

        <h2>Changes</h2>
        <p>
          If this policy changes materially, the version at the top changes with it and you will be
          asked to accept the new wording. The version you accepted when you signed up is recorded
          against your account.
        </p>
      </div>
    </div>
  )
}
